/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable object-curly-newline */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useState, useCallback, useEffect, useContext,
} from 'react';
import { useLocation, useHistory, Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { UserContext } from '../../contexts/User';
import API from '../../api/endpoints';
import { sendRequest } from '../../api/shootAPI';
import { Navbar, Sidebar } from '../../components';
import Toggle from '../../components/Toggle';
import DataTable from '../../components/DataTable';
import { NotificationContext } from '../../contexts/Notification';
import useDebounce from '../../hooks/useDebounce';
import { webcamAuditPeriods } from '../../constants/webcamAudit';
import MemberCell from './MemberCell';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../utils/styledSelect';
import './webcamAudit.css';

const PAGE_SIZE = 10;

function WebcamAudit() {
  const { search } = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(search);
  const page = query.get('page');
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace, setActiveWorkspace } = useContext(UserContext);

  const [members, setMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { debounce } = useDebounce();
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const changeWebcamAuditsPerPeriod = async (newValue, memberId) => {
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.memberUpdateWebcamAuditsPerPeriod(activeWorkspace.id, memberId),
      data: { webcamAuditsPerPeriod: newValue },
    });
    if (!response.success) {
      displayNotification({
        message: 'There was an issue updating webcam preferences. Please try again',
        severity: 'error',
      });
    }
  };

  const getMembers = useCallback(async () => {
    if (!activeWorkspace) return;
    setIsLoading(true);
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.webcamAuditLatest(activeWorkspace.id),
      params: {
        pageSize: PAGE_SIZE,
        page: currentPage,
        name: searchQuery,
        teamId: selectedTeamId,
        ...(sortKey ? { sortKey, sortOrder: sortDir } : {}),
      },
    });
    if (response.success) {
      setMembers(response.data?.members ?? []);
      setTotalPages(response.data?.pageInfo?.totalPages ?? 1);
    }
    setIsLoading(false);
  }, [activeWorkspace, currentPage, searchQuery, selectedTeamId, sortKey, sortDir]);

  const getTeams = useCallback(async () => {
    if (!activeWorkspace) return;
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getAllTeamInfo(activeWorkspace.id),
      timeout: 60000,
    });
    if (response.success) {
      setTeams(response.data.teams);
    }
  }, [activeWorkspace]);

  useEffect(() => { getMembers(); }, [getMembers]);
  useEffect(() => { getTeams(); }, [getTeams]);

  const changeDefaultWebcamAuditsPerPeriod = async (newValue) => {
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.workspaceUpdateWebcamAuditsPerPeriod(activeWorkspace.id),
      data: { webcamAuditsPerPeriod: newValue },
    });
    if (response.success) {
      setActiveWorkspace({ ...activeWorkspace, webcamAuditsPerPeriod: newValue });
    } else {
      displayNotification({
        message: 'There was an issue updating webcam preferences. Please try again',
        severity: 'error',
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchDebounced = useCallback(debounce((value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, 500), []);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    handleSearchDebounced(e.target.value);
  };

  const handlePageChange = (next) => {
    setCurrentPage(next);
    history.push(`?page=${next}`);
  };

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const columns = [
    {
      key: 'name',
      label: 'User',
      sortable: true,
      width: 280,
      flex: true,
      render: (m) => (
        <MemberCell
          id={m.id}
          firstName={m.user?.firstName}
          lastName={m.user?.lastName}
          email={m.user?.email}
          profilePicture={m.user?.profilePicture}
        />
      ),
    },
    {
      key: 'frequency',
      label: 'Webcam Frequency',
      width: 340,
      render: (m) => (
        <Toggle
          activeLabel={m.webcamAuditsPerPeriod}
          options={webcamAuditPeriods.map((period) => ({
            label: period,
            onClick: () => changeWebcamAuditsPerPeriod(period, m.id),
          }))}
        />
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 160,
      align: 'right',
      render: (m) => (
        <Link
          to={{
            pathname: '/members/activity/view',
            search: `?member=${m.id}`,
            state: { workspaceMember: m, activityType: 'Webcam Audit' },
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="inline-flex items-center justify-center px-3 py-1.5 rounded-[8px] text-[11px] font-semibold uppercase tracking-wider text-ink-900 bg-white border border-rule hover:border-brand-500 hover:text-brand-700 transition whitespace-nowrap"
            style={{ borderStyle: 'solid', letterSpacing: '0.05em' }}
          >
            View Activity
          </button>
        </Link>
      ),
    },
  ];

  return (
    <div>
      <Navbar />
      <Sidebar />
      <div className="screen-wrapper">
        <div className="screen-header" style={{ fontFamily: 'Inter, sans-serif' }}>
          {/* Header — title left, controls top-right */}
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between mb-5">
            <div className="min-w-0">
              <h1 className="font-bold text-ink-900 leading-tight truncate text-2xl lg:text-[32px] lg:leading-10" data-testid="title" data-tour="webcam-audit">
                Webcam Audit
              </h1>
              <p className="text-sm text-ink-500 mt-1" data-testid="subtitle">
                Control webcam audit settings for workspace members
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 flex-shrink-0 xl:justify-end">
              <div className="relative" style={{ height: 40, width: 260 }}>
                <Search size={14} className="text-ink-500 pointer-events-none" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} />
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder="Search for user"
                  className="w-full rounded-[8px] bg-white text-ink-900 placeholder:text-ink-500 border border-rule focus:outline-none focus:border-brand-500"
                  style={{ height: 40, paddingLeft: 36, paddingRight: 12, fontSize: 14, lineHeight: '20px', borderStyle: 'solid', boxSizing: 'border-box' }}
                />
              </div>
              <select
                value={selectedTeamId}
                onChange={(e) => { setSelectedTeamId(Number(e.target.value)); setCurrentPage(1); }}
                className={`${STYLED_SELECT_CLASS} min-w-[160px]`}
                style={STYLED_SELECT_STYLE}
              >
                <option value={0}>All Teams</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>

          {/* Organization Default */}
          <div className="bg-white border border-rule rounded-[12px] p-4 mb-5" style={{ borderStyle: 'solid' }}>
            <h4 className="text-sm font-semibold text-ink-900 mb-1">Organization Default</h4>
            <p className="text-xs text-ink-500 mb-3">Applied to all members unless overridden below</p>
            <div style={{ maxWidth: 360 }}>
              <Toggle
                activeLabel={activeWorkspace?.webcamAuditsPerPeriod}
                options={webcamAuditPeriods.map((period) => ({
                  label: period,
                  onClick: () => changeDefaultWebcamAuditsPerPeriod(period),
                }))}
              />
            </div>
          </div>

          {/* Individual Settings */}
          <div className="mb-3">
            <h4 className="text-sm font-semibold text-ink-900">Individual Settings</h4>
            <p className="text-xs text-ink-500">Override the organization default for specific members</p>
          </div>

          <DataTable
            columns={columns}
            rows={members}
            getRowKey={(m) => m.id}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={toggleSort}
            loading={isLoading}
            emptyText="No members found."
            page={currentPage}
            pageSize={PAGE_SIZE}
            total={totalPages * PAGE_SIZE}
            onPageChange={handlePageChange}
            minWidth={820}
          />
        </div>
      </div>
    </div>
  );
}

export default WebcamAudit;
